import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Ruler, MessageCircle } from 'lucide-react';
import { trackPageView } from '../lib/analytics';

export default function SizeGuidePage() {
  useEffect(() => { trackPageView('/guia-de-tallas', 'Guía de Tallas'); }, []);

  return (
    <div className="min-h-screen bg-black pt-24" data-testid="size-guide-page">
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gold/10 mb-6">
              <Ruler className="w-8 h-8 text-gold" />
            </div>
            <h1 className="text-4xl md:text-5xl font-serif text-white mb-6">Guía de Tallas</h1>
            <p className="text-white/60 max-w-2xl mx-auto">
              Encontrar tu talla perfecta es esencial para que tu traje de baño te quede increíble. 
              Sigue nuestra guía para tomar tus medidas correctamente.
            </p>
          </motion.div>

          {/* How to Measure */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mb-16"
          >
            <h2 className="text-2xl font-serif text-white mb-8 text-center">¿Cómo tomar tus medidas?</h2>
            
            <div className="grid md:grid-cols-3 gap-6">
              {[
                {
                  title: 'Busto',
                  description: 'Mide la parte más ancha de tu busto, pasando la cinta métrica por debajo de tus brazos y alrededor de tu espalda.',
                  tip: 'Mantén la cinta horizontal y no aprietes demasiado.'
                },
                {
                  title: 'Cintura',
                  description: 'Mide la parte más estrecha de tu torso, generalmente justo encima del ombligo.',
                  tip: 'Respira normalmente y no contengas el aire.'
                },
                {
                  title: 'Cadera',
                  description: 'Mide la parte más ancha de tus caderas, pasando la cinta por la zona de los glúteos.',
                  tip: 'Mantén los pies juntos para mayor precisión.'
                }
              ].map((item, index) => (
                <div key={index} className="p-6 bg-[#0A0A0A] border border-white/10">
                  <div className="w-10 h-10 bg-gold/10 flex items-center justify-center mb-4">
                    <span className="text-gold font-serif font-bold">{index + 1}</span>
                  </div>
                  <h3 className="text-lg font-medium text-white mb-2">{item.title}</h3>
                  <p className="text-white/60 text-sm mb-4">{item.description}</p>
                  <p className="text-gold text-xs italic">Tip: {item.tip}</p>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Size Chart */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mb-16"
          >
            <h2 className="text-2xl font-serif text-white mb-8 text-center">Tabla de Tallas (cm)</h2>
            
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="size-table">
                <thead>
                  <tr className="border-b border-white/20">
                    <th className="py-4 px-6 text-left text-gold font-medium">Talla</th>
                    <th className="py-4 px-6 text-center text-gold font-medium">Busto</th>
                    <th className="py-4 px-6 text-center text-gold font-medium">Cintura</th>
                    <th className="py-4 px-6 text-center text-gold font-medium">Cadera</th>
                    <th className="py-4 px-6 text-center text-gold font-medium hidden sm:table-cell">Talla US</th>
                  </tr>
                </thead>
                <tbody className="text-white/80">
                  <tr className="border-b border-white/5 hover:bg-white/5 transition-colors">
                    <td className="py-4 px-6 font-medium text-white">XS</td>
                    <td className="py-4 px-6 text-center">80 - 84</td>
                    <td className="py-4 px-6 text-center">60 - 64</td>
                    <td className="py-4 px-6 text-center">86 - 90</td>
                    <td className="py-4 px-6 text-center hidden sm:table-cell">0 - 2</td>
                  </tr>
                  <tr className="border-b border-white/5 hover:bg-white/5 transition-colors">
                    <td className="py-4 px-6 font-medium text-white">S</td>
                    <td className="py-4 px-6 text-center">84 - 88</td>
                    <td className="py-4 px-6 text-center">64 - 68</td>
                    <td className="py-4 px-6 text-center">90 - 94</td>
                    <td className="py-4 px-6 text-center hidden sm:table-cell">4 - 6</td>
                  </tr>
                  <tr className="border-b border-white/5 hover:bg-white/5 transition-colors">
                    <td className="py-4 px-6 font-medium text-white">M</td>
                    <td className="py-4 px-6 text-center">88 - 92</td>
                    <td className="py-4 px-6 text-center">68 - 72</td>
                    <td className="py-4 px-6 text-center">94 - 98</td>
                    <td className="py-4 px-6 text-center hidden sm:table-cell">8 - 10</td>
                  </tr>
                  <tr className="border-b border-white/5 hover:bg-white/5 transition-colors">
                    <td className="py-4 px-6 font-medium text-white">L</td>
                    <td className="py-4 px-6 text-center">92 - 96</td>
                    <td className="py-4 px-6 text-center">72 - 76</td>
                    <td className="py-4 px-6 text-center">98 - 102</td>
                    <td className="py-4 px-6 text-center hidden sm:table-cell">12 - 14</td>
                  </tr>
                  <tr className="hover:bg-white/5 transition-colors">
                    <td className="py-4 px-6 font-medium text-white">XL</td>
                    <td className="py-4 px-6 text-center">96 - 100</td>
                    <td className="py-4 px-6 text-center">76 - 80</td>
                    <td className="py-4 px-6 text-center">102 - 106</td>
                    <td className="py-4 px-6 text-center hidden sm:table-cell">16 - 18</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </motion.div>

          {/* Tips */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="mb-16 p-8 bg-gold/5 border border-gold/20"
          >
            <h3 className="text-xl font-serif text-gold mb-4">Consejos para elegir tu talla</h3>
            <ul className="space-y-3 text-white/70">
              <li className="flex items-start">
                <span className="text-gold mr-2">•</span>
                Si estás entre dos tallas, te recomendamos elegir la talla más grande para mayor comodidad.
              </li>
              <li className="flex items-start">
                <span className="text-gold mr-2">•</span>
                Nuestros trajes de baño están diseñados con telas que se ajustan al cuerpo pero no aprietan.
              </li>
              <li className="flex items-start">
                <span className="text-gold mr-2">•</span>
                Los bikinis con tirantes ajustables permiten mayor flexibilidad en el ajuste.
              </li>
              <li className="flex items-start">
                <span className="text-gold mr-2">•</span>
                Si tienes busto grande, considera un top con mayor soporte o tirantes más anchos.
              </li>
            </ul>
          </motion.div>

          {/* Help CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="text-center p-8 bg-[#0A0A0A] border border-white/10"
          >
            <h2 className="text-xl font-serif text-white mb-4">¿Necesitas ayuda para elegir tu talla?</h2>
            <p className="text-white/60 mb-6">
              Si aún tienes dudas, escríbenos por WhatsApp y te asesoramos personalmente para encontrar tu talla perfecta.
            </p>
            <a 
              href="https://wa.me/584242925792?text=Hola!%20Necesito%20ayuda%20para%20elegir%20mi%20talla"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-gold-solid inline-flex items-center justify-center gap-2"
            >
              <MessageCircle className="w-5 h-5" />
              Asesoría por WhatsApp
            </a>
          </motion.div>

          {/* Back to Shop */}
          <div className="mt-12 text-center">
            <Link to="/tienda" className="text-gold hover:text-gold-hover transition-colors">
              ← Volver a la Tienda
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
